(() => {
  'use strict';

  const MEDIA = '.product__media[data-media-id]';
  const THUMB = '.product__thumbnail[data-media-id]';
  const ATTRIBUTES = ['src', 'srcset', 'data-src', 'data-srcset', 'alt', 'data-media-id', 'data-gang-connect', 'data-gang-option', 'data-gang-default', 'id', 'class', 'form', 'data-luggex-media-alt-map-id', 'aria-busy', 'hidden', 'style'];
  const string = (value) => value == null ? '' : String(value).trim();
  const id = (value) => string(value).split('/').pop();
  const attr = (node, name) => string(node?.getAttribute?.(name));

  const optionKey = (value) => string(value).toLowerCase().normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '').replace(/['"‘’“”]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  function colorKey(value) {
    const key = string(value).toLowerCase().replace(/^#?color[_-]/, '').replace(/[\s-]+/g, '_')
      .replace(/sliver/g, 'silver').replace(/grey/g, 'gray');
    const aliases = { silver_gray: 'silver', silver: 'silver', navy_blue: 'navy', navy: 'navy', charcoal_metallic: 'charcoal' };
    return aliases[key] || key;
  }

  function colorsInAlt(value) {
    const colors = [];
    const expression = /(?:#(?:show\s+)?|,)\s*(?:[a-z]{2}:)?color[_-]([\w-]+)/gi;
    let match;
    while ((match = expression.exec(string(value)))) colors.push(colorKey(match[1]));
    return [...new Set(colors)];
  }

  function urlKeys(value, base = 'https://www.luggex.com/') {
    if (!value || /^(?:data|blob):/i.test(value)) return [];
    let url;
    try { url = new URL(value, base); } catch (_error) { return []; }
    if (!/^https?:$/.test(url.protocol)) return [];
    let pathname;
    try { pathname = decodeURIComponent(url.pathname); } catch (_error) { pathname = url.pathname; }
    const original = pathname.replace(/_\d+x\d*(?:_crop_[a-z-]+)?(?=\.[^./]+$)/i, '');
    return [...new Set([pathname, original].flatMap((path) => [`${url.host}|${path}`, path, path.split('/').pop()]))];
  }

  function createModel(data) {
    const model = { data, media: new Map(), urls: new Map(), variants: new Map(), owners: new Map(), colorAliases: new Map() };
    model.colorIndex = (data.options || []).findIndex((name) => /^(color|colour)$/i.test(string(name)));
    (data.media || []).forEach((entry) => {
      const media = { ...entry, id: id(entry.id), colors: colorsInAlt(entry.alt) };
      model.media.set(media.id, media);
      (entry.urls || []).forEach((url) => urlKeys(url).forEach((key) => {
        if (!model.urls.has(key)) model.urls.set(key, new Set());
        model.urls.get(key).add(media.id);
      }));
    });
    (data.variants || []).forEach((entry) => {
      const variant = { ...entry, id: id(entry.id), featuredMediaId: id(entry.featuredMediaId), mediaIds: (entry.mediaIds || []).map(id) };
      variant.color = model.colorIndex < 0 ? '' : colorKey(variant.options?.[model.colorIndex]);
      model.variants.set(variant.id, variant);
      if (!variant.featuredMediaId) return;
      if (!model.owners.has(variant.featuredMediaId)) model.owners.set(variant.featuredMediaId, new Set());
      model.owners.get(variant.featuredMediaId).add(variant.id);
      const colors = model.media.get(variant.featuredMediaId)?.colors || [];
      // Infer legacy tokens from real variant bindings, never a SKU or a URL.
      // A token bound to conflicting colors is deliberately left unresolved.
      colors.forEach((color) => {
        if (!model.colorAliases.has(color)) model.colorAliases.set(color, new Set());
        model.colorAliases.get(color).add(variant.color);
      });
    });
    return model;
  }

  function imageCandidates(image) {
    if (!image) return [];
    const values = [attr(image, 'src'), image.src, attr(image, 'data-src'), image.currentSrc];
    ['srcset', 'data-srcset'].forEach((name) => attr(image, name).split(',').forEach((part) => values.push(part.trim().split(/\s+/)[0])));
    return [...new Set(values.filter(Boolean))];
  }

  function resolveMedia(node, model) {
    const image = node?.matches?.('img') ? node : node?.querySelector?.('img');
    // StarApps can retain an old media ID while replacing src. The explicit
    // src wins even if currentSrc still points at the previous image loading.
    for (const url of imageCandidates(image)) {
      for (const key of urlKeys(url, node?.ownerDocument?.baseURI)) {
        const hits = model.urls.get(key);
        if (hits?.size === 1) return model.media.get([...hits][0]);
      }
    }
    return model.media.get(id(attr(node, 'data-media-id'))) || { id: id(attr(node, 'data-media-id')), alt: attr(image, 'alt'), colors: colorsInAlt(attr(image, 'alt')) };
  }

  function colorMatches(token, variant, model) {
    if (token === variant.color) return true;
    const aliases = model.colorAliases.get(token);
    return aliases?.size === 1 && aliases.has(variant.color);
  }

  function shouldShow(media, variant, model, node) {
    if (!variant) return true;
    if (media.id && media.id === variant.featuredMediaId) return true;
    if (variant.mediaIds.length) return variant.mediaIds.includes(media.id);
    const owners = model.owners.get(media.id);
    if (owners?.size) return owners.has(variant.id);
    if (media.colors?.length && variant.color && !media.colors.some((token) => colorMatches(token, variant, model))) return false;
    // Some accessories group both Size and Color in their server ALT. Read
    // every option constraint, since native data-gang-connect holds only one.
    const group = string(media.alt).split('#').slice(1).join(',').split(',');
    for (let index = 0; index < (model.data.options || []).length; index++) {
      const optionName = optionKey(model.data.options[index]);
      if (/^(color|colour)$/.test(optionName)) continue;
      const prefix = `${optionName}_`;
      const values = group.map((value) => optionKey(value.trim().replace(/^[a-z]{2}:/i, ''))).filter((value) => value.startsWith(prefix));
      if (values.length && !values.includes(`${prefix}${optionKey(variant.options?.[index])}`)) return false;
    }
    const connection = attr(node, 'data-gang-connect');
    const option = attr(node, 'data-gang-option');
    if (option && connection && !/^(color|colour)$/i.test(option)) {
      const optionIndex = (model.data.options || []).findIndex((name) => optionKey(name) === optionKey(option));
      if (optionIndex >= 0) {
        const expected = `${optionKey(option)}_${optionKey(variant.options?.[optionIndex])}`;
        return connection.split(',').some((value) => optionKey(value.replace(/^[a-z]{2}:/i, '')) === expected);
      }
    }
    // Untagged details and videos are shared. Missing ALT is not evidence
    // that a product image belongs to another color.
    return true;
  }

  function selectionKey(variant) {
    return variant ? [variant.color, variant.featuredMediaId, [...variant.mediaIds].sort().join(',')].join('|') : '';
  }

  function createRuntime(root) {
    const document = root.document;
    const states = new Map();
    let queued = false;
    const style = document.createElement('style');
    style.id = 'luggex-color-gallery-visibility';
    style.textContent = `
      media-gallery[data-luggex-active-gallery="true"] { visibility:visible!important; opacity:1!important; display:block!important; }
      media-gallery[data-luggex-active-gallery="false"] { display:none!important; }
      media-gallery .product__media[data-luggex-color-visible][data-gang-default]:not([hidden]) { display:flex; }
      media-gallery .product__thumbnail[data-luggex-color-visible][data-gang-default]:not([hidden]) { display:block; }
      media-gallery .product__media--variant[data-luggex-color-featured="true"]:not([hidden]) { display:flex; }
      @media(min-width:1280px) {
        media-gallery[data-luggex-active-gallery="true"].xl\\:grid { display:grid!important; }
        media-gallery .product__media[data-luggex-color-visible].xl\\:hidden { display:none!important; }
      }`;
    (document.head || document.documentElement).appendChild(style);

    const set = (node, name, value) => {
      if (attr(node, name) !== value) node.setAttribute(name, value);
    };
    const primary = (gallery) => [...(gallery?.querySelectorAll?.(`slider-element ${MEDIA}`) || [])].filter((node) => node.closest('media-gallery') === gallery);
    const thumbnails = (gallery) => [...(gallery?.querySelectorAll?.(THUMB) || [])].filter((node) => node.closest('media-gallery') === gallery);
    const visible = (node) => !node.hidden && node.style.display !== 'none';
    const mediaKey = (node, state) => resolveMedia(node, state.model).id;
    const sliderVisible = (gallery) => {
      const slider = gallery?.querySelector('slider-element');
      return slider?.itemsToShow ? [...slider.itemsToShow] : primary(gallery).filter((node) => visible(node) && node.clientWidth > 0);
    };

    function mapFor(gallery) {
      const node = document.getElementById(attr(gallery, 'data-luggex-media-alt-map-id')) || gallery.querySelector('script[data-luggex-media-alt-map]');
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (_error) { return null; }
    }

    function scopeFor(gallery) {
      return gallery.closest('.product, .featured-product, quick-view, x-modal, .shopify-section') || gallery.parentElement;
    }

    function relatedGalleries(state) {
      return [...state.scope.querySelectorAll('media-gallery')].filter((gallery) => gallery !== state.original && (
        gallery.id === `${state.original.id}-starapps` ||
        (gallery.classList.contains('starapps') && attr(gallery, 'form') === state.formId && attr(gallery, 'data-luggex-media-alt-map-id') === state.mapId)
      ));
    }

    function selectedVariant(state) {
      const form = document.getElementById(state.formId);
      const info = form?.closest('product-info');
      const pickers = [...(info || state.scope).querySelectorAll('variant-picker')];
      const picker = pickers.find((node) => !node.id || node.id.includes(string(state.model.data.productId))) || pickers[0];
      const options = [...(picker?.querySelectorAll('[data-option-index][data-option-slug]') || [])].map((container) => {
        const selected = container.querySelector('input:checked, option:checked, option[selected]');
        return [parseInt(attr(container, 'data-option-index').replace('option', ''), 10) - 1, attr(selected, 'data-option-value') || selected?.value];
      });
      if (options.length) {
        const matched = [...state.model.variants.values()].find((variant) => options.every(([index, value]) => string(variant.options?.[index]) === string(value)));
        if (matched) return matched;
      }
      const formVariant = id(form?.querySelector('[name="id"]')?.value);
      return state.model.variants.get(formVariant) || state.model.variants.get(state.eventVariantId) || state.model.variants.get(id(state.model.data.selectedVariantId));
    }

    function applyVisibility(gallery, state, variant) {
      let changed = false;
      const groups = [primary(gallery), thumbnails(gallery), [...gallery.querySelectorAll(`.product__preview ${MEDIA}`)]];
      groups.forEach((nodes) => {
        const seen = new Set();
        nodes.forEach((node) => {
          const media = resolveMedia(node, state.model);
          const show = shouldShow(media, variant, state.model, node) && (!media.id || !seen.has(media.id));
          if (show && media.id) seen.add(media.id);
          if (node.hidden !== !show) { node.hidden = !show; changed = true; }
          if (show) {
            if (node.style.display === 'none') { node.style.removeProperty('display'); changed = true; }
            set(node, 'data-luggex-color-visible', 'true');
            set(node, 'data-luggex-color-featured', media.id === variant?.featuredMediaId ? 'true' : 'false');
            node.querySelectorAll('img[data-src]').forEach((image) => {
              if (!attr(image, 'src')) image.setAttribute('src', attr(image, 'data-src'));
              if (!attr(image, 'srcset') && attr(image, 'data-srcset')) image.setAttribute('srcset', attr(image, 'data-srcset'));
            });
          } else if (node.style.display !== 'none' || node.style.getPropertyPriority('display') !== 'important') {
            node.style.setProperty('display', 'none', 'important'); changed = true;
          }
        });
      });
      return changed;
    }

    function physicalItem(gallery) {
      const slider = gallery?.querySelector('slider-element');
      const items = sliderVisible(gallery);
      if (!items.length || !slider) return null;
      if (slider.scrollWidth > slider.clientWidth + 1 || Math.abs(slider.scrollLeft) > 1) {
        // Native currentPage is debounced and late init resets it to 1. The
        // actual scroll position is authoritative during clone handovers.
        return items.reduce((closest, node) => Math.abs(node.offsetLeft - items[0].offsetLeft - slider.scrollLeft) <
          Math.abs(closest.offsetLeft - items[0].offsetLeft - slider.scrollLeft) ? node : closest, items[0]);
      }
      return items[(slider.currentPage || 1) - 1] || items[0];
    }

    function currentMedia(state) {
      const current = physicalItem(state.active);
      return current ? mediaKey(current, state) : state.selectedMediaId;
    }

    function updateDots(gallery, state, selectedId) {
      gallery.querySelectorAll('media-dots').forEach((dots) => {
        dots.reset?.();
        let index = 1;
        thumbnails(gallery).filter((node) => dots.contains(node) && visible(node)).forEach((node) => {
          set(node, 'data-index', String(index++));
          set(node, 'aria-current', mediaKey(node, state) === selectedId ? 'true' : 'false');
        });
      });
    }

    function selectMedia(gallery, state, requestedId) {
      const slider = gallery.querySelector('slider-element');
      const items = sliderVisible(gallery);
      const matching = items.find((node) => mediaKey(node, state) === requestedId);
      // The native gallery morph may arrive after the option input changes.
      // Keep waiting for a genuinely missing cover instead of committing to
      // a detail image. A responsive xl:hidden cover still exists in markup
      // and is displayed by the separate desktop preview.
      if (requestedId && !matching && !primary(gallery).some((node) => mediaKey(node, state) === requestedId)) return false;
      const target = matching || items[0];
      if (!target || !slider) return false;
      const index = items.indexOf(target) + 1;
      const position = target.offsetLeft - items[0].offsetLeft;
      if (slider.currentPage === index && Math.abs(slider.scrollLeft - position) < 1) {
        slider.scrollPosition = position;
        slider.adjustHeight?.();
        slider.updateButtons?.();
        state.selectedMediaId = mediaKey(target, state);
        updateDots(gallery, state, state.selectedMediaId);
        return true;
      }
      // After filtering, old currentPage and new itemOffset cannot safely be
      // combined by the native relative select(). Scroll to the actual node.
      slider.currentPage = index;
      slider.scrollPosition = position;
      if (typeof slider.scrollTo === 'function') slider.scrollTo({ left: slider.scrollPosition, behavior: 'instant' });
      else slider.select?.(index, true);
      items.forEach((node) => node.classList.toggle('selected', node === target));
      slider.adjustHeight?.();
      slider.updateButtons?.();
      slider.dispatchEventHandler?.();
      state.selectedMediaId = mediaKey(target, state);
      updateDots(gallery, state, state.selectedMediaId);
      return true;
    }

    function sync(state) {
      if (!state.original.isConnected || !state.scope.isConnected) { states.delete(state.mapId); state.observer?.disconnect(); return; }
      const data = mapFor(state.original);
      if (!data?.variants?.length) return;
      if (state.mapText !== JSON.stringify(data)) { state.mapText = JSON.stringify(data); state.model = createModel(data); }
      const variant = selectedVariant(state);
      if (!variant) return;
      const key = selectionKey(variant);
      const switched = state.selectionKey !== key;
      if (state.nativeMorphKey !== key) state.nativeMorphPreserve = '';
      const preserve = switched ? '' : currentMedia(state);
      const clones = relatedGalleries(state);
      let changed = applyVisibility(state.original, state, variant);
      const required = new Set(primary(state.original).filter(visible).map((node) => mediaKey(node, state)));
      const defaultFeatured = id(state.model.data.defaultFeaturedMediaId);
      const fallbackMedia = state.model.media.get(defaultFeatured);
      const eligible = primary(state.original).filter(visible);
      const firstColorMedia = eligible.find((node) => resolveMedia(node, state.model).colors?.some((token) => colorMatches(token, variant, state.model)));
      const desiredFeatured = variant.featuredMediaId || (fallbackMedia && shouldShow(fallbackMedia, variant, state.model) ? defaultFeatured : mediaKey(firstColorMedia || eligible[0], state));
      if (desiredFeatured) required.add(desiredFeatured);
      const clone = clones.find((gallery) => {
        changed = applyVisibility(gallery, state, variant) || changed;
        const available = new Set(primary(gallery).filter(visible).map((node) => mediaKey(node, state)));
        // A truncated clone may not discard the cover or common photos/videos.
        // Retain the intact native gallery rather than manufacture duplicates.
        return available.size && [...required].every((mediaId) => available.has(mediaId));
      });
      const active = clone || state.original;
      const handover = state.active !== active;
      [state.original, ...clones].forEach((gallery) => {
        set(gallery, 'data-luggex-active-gallery', gallery === active ? 'true' : 'false');
        if (gallery === active && gallery.style.display === 'none') gallery.style.removeProperty('display');
        if (gallery === active && gallery.hidden) gallery.hidden = false;
      });
      const host = active.closest('.product__gallery-container');
      if (host && host.style.height && host.style.height !== 'auto') host.style.setProperty('height', 'auto');
      active.querySelector('slider-element')?.reset?.();
      if (changed) active.countMediaGallery?.();
      state.active = active;
      state.selectionKey = key;
      const ids = primary(active).filter(visible).map((node) => mediaKey(node, state)).join(',');
      const topologyChanged = ids !== state.mediaSet;
      state.mediaSet = ids;
      if (switched || handover || (topologyChanged && preserve)) state.pendingSelection = (switched ? desiredFeatured : preserve) || desiredFeatured;
      if (state.pendingSelection && selectMedia(active, state, state.pendingSelection)) state.pendingSelection = '';
      else {
        const physical = physicalItem(active);
        const slider = active.querySelector('slider-element');
        if (!state.pendingSelection && physical && slider) {
          const index = sliderVisible(active).indexOf(physical) + 1;
          if (slider.currentPage !== index) { slider.currentPage = index; slider.updateButtons?.(); slider.adjustHeight?.(); }
          state.selectedMediaId = mediaKey(physical, state);
        }
        updateDots(active, state, state.selectedMediaId || preserve);
      }
      if (!state.original.hasAttribute('aria-busy') && !state.pendingSelection) state.nativeMorphPreserve = '';
      // Idempotent writes plus consuming our records avoid feedback loops;
      // the next third-party src/ALT/style change remains observable.
      state.observer?.takeRecords();
      if (handover) state.stabilizeFrames = 2;
      if (state.stabilizeFrames > 0) { state.stabilizeFrames--; schedule(); }
    }

    function schedule() {
      if (queued) return;
      queued = true;
      root.requestAnimationFrame(() => { queued = false; states.forEach(sync); });
    }

    function register(gallery) {
      if (gallery.classList.contains('starapps') || /-starapps$/.test(gallery.id)) return;
      const data = mapFor(gallery);
      if (!data?.variants?.length) return;
      const mapId = attr(gallery, 'data-luggex-media-alt-map-id') || gallery.id;
      const existing = states.get(mapId);
      if (existing?.original === gallery && existing.scope === scopeFor(gallery)) return;
      // A third party may insert its clone before adding the starapps suffix
      // or class. Keep the existing connected native owner during that gap.
      if (existing?.original?.isConnected && existing.original !== gallery) return;
      const state = { ...(existing || {}), mapId, original: gallery, scope: scopeFor(gallery), formId: attr(gallery, 'form'), model: createModel(data), mapText: JSON.stringify(data) };
      existing?.observer?.disconnect();
      if (root.MutationObserver) {
        state.observer = new root.MutationObserver((records) => {
          if (state.nativeMorphPreserve && records.some((record) => record.target === state.original && record.attributeName === 'aria-busy' && !state.original.hasAttribute('aria-busy'))) state.pendingSelection = state.nativeMorphPreserve;
          if (records.some((record) => record.type === 'attributes' ? record.target.closest?.('media-gallery, variant-picker') :
            [...record.addedNodes, ...record.removedNodes].some((node) => node.matches?.('media-gallery, [data-media-id], img, variant-picker') || node.querySelector?.('media-gallery, [data-media-id], img, variant-picker')))) schedule();
        });
        state.observer.observe(state.scope, { childList: true, subtree: true, attributes: true, attributeFilter: ATTRIBUTES });
      }
      states.set(mapId, state);
    }

    function scan(node = document) {
      if (node?.matches?.('media-gallery')) register(node);
      node?.querySelectorAll?.('media-gallery').forEach(register);
      schedule();
    }

    document.addEventListener('variant:change', (event) => {
      const eventId = id(event.detail?.variant?.id);
      states.forEach((state) => {
        const formMatches = event.target?.id === state.formId || event.detail?.formId === state.formId;
        const local = event.target !== document && state.scope.contains(event.target);
        if ((formMatches || local) && state.model.variants.has(eventId)) {
          state.eventVariantId = eventId;
          // ProductInfo's native morph unconditionally scrolls to zero, even
          // when Size changes reuse the same color/cover. Capture the previous
          // media from state before its delayed scroll event overwrites it.
          const key = selectionKey(state.model.variants.get(eventId));
          const prior = state.variantIntent?.selectionKey === key ? state.variantIntent.mediaId : state.selectedMediaId;
          if (!state.pendingSelection && prior && key === state.selectionKey) {
            state.pendingSelection = prior;
            state.nativeMorphPreserve = prior;
            state.nativeMorphKey = key;
          }
          state.variantIntent = null;
        }
      });
      schedule();
    }, true);
    document.addEventListener('change', (event) => {
      if (!event.target?.matches?.('[data-option-value], variant-picker input, variant-picker select, input[name="id"]')) return;
      if (!event.target.matches('input[name="id"]')) states.forEach((state) => {
        if (state.scope.contains(event.target)) state.variantIntent = { selectionKey: state.selectionKey, mediaId: state.selectedMediaId || currentMedia(state) };
      });
      schedule();
    }, true);
    document.addEventListener('slider:change', (event) => {
      states.forEach((state) => {
        if (event.target?.closest('media-gallery') !== state.active) return;
        const current = event.detail?.currentElement;
        if (current) {
          state.selectedMediaId = mediaKey(current, state);
          if (state.nativeMorphPreserve && state.original.hasAttribute('aria-busy')) state.nativeMorphPreserve = state.selectedMediaId;
        }
      });
    }, true);
    document.addEventListener('scroll', (event) => {
      if (!event.target?.matches?.('slider-element')) return;
      states.forEach((state) => {
        if (event.target.closest('media-gallery') !== state.active) return;
        const current = physicalItem(state.active);
        if (current) state.selectedMediaId = mediaKey(current, state);
      });
    }, true);
    ['shopify:section:load', 'quickview:loaded', 'quickview:open'].forEach((name) => document.addEventListener(name, (event) => scan(event.detail?.root || event.target), true));
    root.addEventListener?.('resize', schedule);
    if (root.MutationObserver && document.documentElement) {
      const observer = new root.MutationObserver((records) => {
        records.forEach((record) => record.addedNodes.forEach((node) => {
          if (node.matches?.('media-gallery, script[data-luggex-media-alt-map]') || node.querySelector?.('media-gallery, script[data-luggex-media-alt-map]')) scan(document);
        }));
        states.forEach((state) => { if (!state.original.isConnected) { state.observer?.disconnect(); states.delete(state.mapId); } });
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
    scan();
    return { scan, sync: () => states.forEach(sync), states };
  }

  const api = { colorKey, optionKey, colorsInAlt, urlKeys, createModel, resolveMedia, shouldShow, selectionKey, createRuntime };
  if (typeof module === 'object' && module.exports) module.exports = api;
  const root = typeof window !== 'undefined' ? window : null;
  if (!root?.document || root.__luggexColorGallery) return;
  root.__luggexColorGallery = createRuntime(root);
  root.luggexColorGalleryBound = true;
  if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', () => root.__luggexColorGallery.scan(), { once: true });
})();
